/**
 * User Service
 *
 * Handles GraphQL operations for user management:
 * - Fetching users by client
 * - Getting individual user details
 * - Creating new users (with Cognito integration)
 * - Updating user details
 * - Deactivating users
 */

import { API } from 'aws-amplify';
import config from '../../../../../aws-exports';
import { listUsers as listUsersQuery } from '../../../../../graphql/queries';
import {
  createUser as createUserMutation,
  updateUser as updateUserMutation,
  createCognitoUser as createCognitoUserMutation,
  deleteCognitoUser as deleteCognitoUserMutation
} from '../../../../../graphql/mutations';

/**
 * Fetch all users for a specific client
 *
 * @param {string} clientId - Client ID to filter users
 * @returns {Promise<Array>} Array of user objects
 * @throws {Error} If the GraphQL query fails
 */
export async function listUsers(clientId) {
  if (!clientId) {
    throw new Error('clientId is required');
  }

  try {
    const allUsers = [];
    let nextToken = null;

    // Paginate through all users for this client
    do {
      const response = await API.graphql({
        query: listUsersQuery,
        variables: {
          filter: {
            client: {
              eq: clientId,
            },
          },
          limit: 1000,
          nextToken,
        },
      });

      const items = response.data.listUsers.items;
      allUsers.push(...items);
      nextToken = response.data.listUsers.nextToken;
    } while (nextToken);

    // Sort users by userName
    allUsers.sort((a, b) => {
      const nameA = a.userName || '';
      const nameB = b.userName || '';
      return nameA.localeCompare(nameB);
    });

    return allUsers;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw new Error(`Failed to fetch users: ${error.message}`);
  }
}

/**
 * Get a single user by ID
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User object
 * @throws {Error} If the user is not found or query fails
 */
export async function getUser(userId) {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    // Use listUsers with ID filter since we don't have a getUser query
    const response = await API.graphql({
      query: listUsersQuery,
      variables: {
        filter: {
          id: {
            eq: userId,
          },
        },
        limit: 1,
      },
    });

    const users = response.data.listUsers.items;

    if (!users || users.length === 0) {
      throw new Error(`User not found: ${userId}`);
    }

    return users[0];
  } catch (error) {
    console.error('Error fetching user:', error);
    throw new Error(`Failed to fetch user: ${error.message}`);
  }
}

/**
 * Create a new user
 *
 * This function creates both a Cognito user account and a database user record.
 * If database creation fails, the Cognito user is automatically rolled back.
 *
 * @param {Object} userData - User data
 * @param {string} userData.client - Client ID
 * @param {string} userData.userName - Username
 * @param {string} userData.firstName - First name
 * @param {string} userData.lastName - Last name
 * @param {string} userData.email - Email address
 * @param {string} userData.groupRole - Group role (APP_ADMIN, CLIENT_ADMIN, MANAGER, ASSOCIATE)
 * @param {string} [userData.group] - Optional department group
 * @param {string} [userData.employeeId] - Optional employee ID
 * @param {string} [userData.role] - Optional legacy role field (grm client only)
 * @param {string} [userData.tempPassword] - Optional temporary password for Cognito
 * @returns {Promise<Object>} Created user object
 * @throws {Error} If the mutation fails
 */
export async function createUser(userData) {
  const { client, userName, firstName, lastName, email, groupRole, group, employeeId, role, status, tempPassword } = userData;

  // Validation
  if (!client || !userName || !firstName || !lastName || !email || !groupRole) {
    throw new Error('Missing required fields: client, userName, firstName, lastName, email, groupRole');
  }

  let cognitoUserCreated = false;

  try {
    // Step 1: Create user in Cognito
    console.log('Creating Cognito user:', userName);
    const cognitoResponse = await API.graphql({
      query: createCognitoUserMutation,
      variables: {
        userPool: config.aws_user_pools_id,
        userName: userName.toLowerCase(), // Cognito usernames are case-insensitive, normalize to lowercase
        email: email,
        tempPassword: tempPassword || undefined,
      },
    });

    console.log('Successfully created Cognito user:', cognitoResponse);
    cognitoUserCreated = true;

    // Step 2: Create user in database
    try {
      const dbResponse = await API.graphql({
        query: createUserMutation,
        variables: {
          input: {
            client,
            userName: userName.toLowerCase(), // Store as lowercase to match Cognito
            firstName,
            lastName,
            email,
            groupRole,
            group: group || undefined,
            employeeId: employeeId || undefined,
            role: role || undefined,
            status: status || 'ACTIVE', // Use provided status or default to ACTIVE
            securityVersion: 1, // Initial security version
          },
        },
      });

      console.log('Successfully created database user:', dbResponse);
      return dbResponse.data.createUser;

    } catch (dbError) {
      console.error('Error creating user in database:', dbError);

      // Rollback: Delete the Cognito user since database creation failed
      console.log('Rolling back - deleting Cognito user:', userName);
      try {
        await API.graphql({
          query: deleteCognitoUserMutation,
          variables: {
            userPool: config.aws_user_pools_id,
            userName: userName.toLowerCase(),
          },
        });
        console.log('Successfully rolled back Cognito user');
      } catch (rollbackError) {
        console.error('Error rolling back Cognito user:', rollbackError);
        // Continue with original error even if rollback fails
      }

      throw new Error(`Failed to create user in database: ${dbError.message}`);
    }

  } catch (error) {
    console.error('Error in createUser:', error);

    // If Cognito creation failed, throw that error
    if (!cognitoUserCreated) {
      throw new Error(`Failed to create Cognito user: ${error.message}`);
    }

    // Otherwise, rethrow the database/rollback error
    throw error;
  }
}

/**
 * Update an existing user
 *
 * @param {Object} updates - User update data
 * @param {string} updates.id - User ID (required)
 * @param {string} [updates.userName] - Username
 * @param {string} [updates.firstName] - First name
 * @param {string} [updates.lastName] - Last name
 * @param {string} [updates.email] - Email address
 * @param {string} [updates.groupRole] - Group role
 * @param {string} [updates.group] - Department group
 * @param {string} [updates.employeeId] - Employee ID
 * @param {string} [updates.role] - Legacy role field
 * @param {string} [updates.status] - User status (ACTIVE, SUSPENDED, DELETED)
 * @returns {Promise<Object>} Updated user object
 * @throws {Error} If the mutation fails
 */
export async function updateUser(updates) {
  const { id, ...fields } = updates;

  if (!id) {
    throw new Error('id is required for updates');
  }

  try {
    const response = await API.graphql({
      query: updateUserMutation,
      variables: {
        input: {
          id,
          ...fields,
        },
      },
    });

    return response.data.updateUser;
  } catch (error) {
    console.error('Error updating user:', error);
    throw new Error(`Failed to update user: ${error.message}`);
  }
}

/**
 * Deactivate a user (soft delete)
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Updated user object with status=DELETED
 * @throws {Error} If the mutation fails
 */
export async function deactivateUser(userId) {
  if (!userId) {
    throw new Error('userId is required');
  }

  return updateUser({
    id: userId,
    status: 'DELETED',
  });
}
